import nipplejs from 'nipplejs';
import { Utils } from '../utils/Utils';
import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export class InputManager {
    constructor(joystickZone, playerCar) {
        this.joystickZone = joystickZone;
        this.playerCar = playerCar;
        
        // Input state
        this.inputState = {
            forward: 0,
            backward: 0,
            left: 0,
            right: 0,
            jump: false,
            boost: false
        };
        
        // Keep track of pressed keys to handle multiple keys
        this.pressedKeys = new Set();
        
        // Mobile joystick
        this.joystickManager = null;
        
        // Debug flag - always enable on deployed site too
        this.debug = true;
        
        // Initialize handler methods (will be bound in setup methods)
        this.handleKeyDown = null;
        this.handleKeyUp = null;
        this.handleJoystickMove = this.handleJoystickMove.bind(this);
        this.handleJoystickEnd = this.handleJoystickEnd.bind(this);
        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
    }
    
    init() {
        console.log('Initializing InputManager');
        // Create debug display first so it's always visible
        this.createDebugDisplay();
        
        // Initialize keyboard controls
        this.setupKeyboardControls();
        
        // Initialize joystick if on mobile
        if (Utils.isMobile()) {
            this.initJoystick();
            
            // Add right side tap for jump/boost
            document.addEventListener('touchstart', this.handleTouchStart);
            document.addEventListener('touchend', this.handleTouchEnd);
        }
        
        // Add keyboard instructions to UI if not on mobile
        if (!Utils.isMobile()) {
            this.addKeyboardInstructions();
        }
        
        // Debug info
        if (this.debug) {
            console.log('Input system initialized. Keyboard events should now be captured.');
        }
        
        // CRITICAL: Force initial control application to ensure car responds immediately
        if (this.playerCar) {
            this.updatePlayerControls();
            console.log('Initial controls applied to player car');
        } else {
            console.warn('Player car reference not available during InputManager init');
        }
        
        // Start a regular update interval with HIGHER frequency (60fps) to ensure controls 
        // are consistently applied regardless of main render loop issues
        this.updateInterval = setInterval(() => {
            this.updatePlayerControls();
            
            // Update debug display regularly to ensure it's always current
            if (this.debug) this.updateDebugDisplay();
        }, 16); // ~60fps
    }
    
    createDebugDisplay() {
        // Remove existing debug display if present
        if (this.debugDisplay && this.debugDisplay.parentNode) {
            this.debugDisplay.parentNode.removeChild(this.debugDisplay);
        }
        
        this.debugDisplay = document.createElement('div');
        this.debugDisplay.className = 'debug-display';
        this.debugDisplay.style.position = 'absolute';
        this.debugDisplay.style.top = '10px';
        this.debugDisplay.style.right = '10px';
        this.debugDisplay.style.background = 'rgba(0,0,0,0.85)';
        this.debugDisplay.style.color = '#00ff00';
        this.debugDisplay.style.padding = '15px';
        this.debugDisplay.style.borderRadius = '5px';
        this.debugDisplay.style.fontFamily = 'monospace';
        this.debugDisplay.style.fontSize = '14px';
        this.debugDisplay.style.fontWeight = 'bold';
        this.debugDisplay.style.zIndex = '2000';
        this.debugDisplay.style.maxWidth = '300px';
        this.debugDisplay.style.boxShadow = '0 0 10px rgba(0,0,0,0.5)';
        this.debugDisplay.style.pointerEvents = 'none'; // So it doesn't interfere with input
        this.debugDisplay.innerHTML = 'Input Debug: No keys pressed yet';
        document.body.appendChild(this.debugDisplay);
        
        // Force an initial update
        this.updateDebugDisplay();
    }
    
    updateDebugDisplay() {
        if (!this.debugDisplay) return;
        
        this.debugDisplay.innerHTML = `
            <span style="color: yellow; font-size: 16px;">INPUT DEBUG</span><br>
            Forward: <span style="color: white;">${this.inputState.forward}</span><br>
            Backward: <span style="color: white;">${this.inputState.backward}</span><br>
            Left: <span style="color: white;">${this.inputState.left}</span><br>
            Right: <span style="color: white;">${this.inputState.right}</span><br>
            Jump: <span style="color: white;">${this.inputState.jump}</span><br>
            Boost: <span style="color: white;">${this.inputState.boost}</span><br>
            <br>
            <span style="color: cyan;">Pressed Keys:</span> ${Array.from(this.pressedKeys).join(', ')}
        `;
    }
    
    addKeyboardInstructions() {
        // Create keyboard instructions element
        const instructions = document.createElement('div');
        instructions.className = 'keyboard-instructions';
        instructions.style.position = 'absolute';
        instructions.style.bottom = '10px';
        instructions.style.left = '10px';
        instructions.style.background = 'rgba(0,0,0,0.7)';
        instructions.style.color = 'white';
        instructions.style.padding = '15px';
        instructions.style.borderRadius = '5px';
        instructions.style.fontFamily = 'Arial, sans-serif';
        instructions.style.zIndex = '1000';
        instructions.style.maxWidth = '300px';
        instructions.style.boxShadow = '0 0 10px rgba(0,0,0,0.5)';
        
        instructions.innerHTML = `
            <div style="font-weight: bold; margin-bottom: 10px; color: yellow;">HOW TO PLAY</div>
            <div class="instruction-item" style="margin: 5px 0;">
                <span style="background: #333; padding: 2px 6px; border-radius: 3px;">Arrow Keys / WASD</span>: Drive
            </div>
            <div class="instruction-item" style="margin: 5px 0;">
                <span style="background: #333; padding: 2px 6px; border-radius: 3px;">Space</span>: Jump
            </div>
            <div class="instruction-item" style="margin: 5px 0;">
                <span style="background: #333; padding: 2px 6px; border-radius: 3px;">Shift</span>: Boost
            </div>
        `;
        
        // Add to document body to ensure it's visible
        document.body.appendChild(instructions);
    }
    
    initJoystick() {
        this.joystickManager = nipplejs.create({
            zone: this.joystickZone,
            mode: 'static',
            position: { left: '50%', top: '50%' },
            color: '#0066ff',
            size: 100
        });
        
        this.joystickManager.on('move', this.handleJoystickMove);
        this.joystickManager.on('end', this.handleJoystickEnd);
    }
    
    setupKeyboardControls() {
        // Clean up existing event listeners if they exist
        if (this.handleKeyDown) {
            window.removeEventListener('keydown', this.handleKeyDown);
        }
        if (this.handleKeyUp) {
            window.removeEventListener('keyup', this.handleKeyUp);
        }
        
        // Create a unified method for handling the keyboard controls
        this.handleKeyDown = (event) => {
            // Prevent default browser behavior for game controls
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 
                 'KeyW', 'KeyS', 'KeyA', 'KeyD', 'Space', 
                 'ShiftLeft', 'ShiftRight', 'MetaLeft', 'MetaRight'].includes(event.code)) {
                event.preventDefault();
            }
            
            // Update debug display even if car is frozen
            this.pressedKeys.add(event.code);
            if (this.debug) this.updateDebugDisplay();
            
            console.log('Key down event detected:', event.code);
            
            if (this.playerCar) {
                // Process key regardless of frozen state for better responsiveness when unfreezing
                switch (event.code) {
                    case 'KeyW':
                    case 'ArrowUp':
                        // Apply full throttle immediately for responsive acceleration
                        this.inputState.forward = 1;
                        console.log('Forward key pressed, inputState updated:', this.inputState);
                        break;
                    case 'KeyS':
                    case 'ArrowDown':
                        this.inputState.backward = 1;
                        console.log('Backward key pressed, inputState updated:', this.inputState);
                        break;
                    case 'KeyA':
                    case 'ArrowLeft':
                        // Apply full turn immediately for responsive turning
                        this.inputState.left = 1;
                        console.log('Left key pressed, inputState updated:', this.inputState);
                        break;
                    case 'KeyD':
                    case 'ArrowRight':
                        // Apply full turn immediately for responsive turning
                        this.inputState.right = 1;
                        console.log('Right key pressed, inputState updated:', this.inputState);
                        break;
                    case 'Space':
                        this.inputState.jump = true;
                        console.log('Jump key pressed, inputState updated:', this.inputState);
                        break;
                    case 'ShiftLeft':
                    case 'ShiftRight':
                    case 'MetaLeft':
                    case 'MetaRight':
                        this.inputState.boost = true;
                        console.log('Boost key pressed, inputState updated:', this.inputState);
                        break;
                }
                
                // Update controls immediately for responsiveness
                this.updatePlayerControls();
            } else {
                console.warn('Player car reference is missing, cannot update controls');
            }
            
            // Handle Escape key (always active)
            if (event.code === 'Escape') {
                event.preventDefault();
                // Check if the game has a UI reference
                if (this.playerCar && this.playerCar.physicsWorld && 
                    this.playerCar.physicsWorld.game && 
                    this.playerCar.physicsWorld.game.ui) {
                    // Toggle the escape menu
                    this.playerCar.physicsWorld.game.ui.togglePauseMenu();
                }
            }
        };
        
        // Ensure handleKeyDown is correctly bound to this instance
        this.handleKeyDown = this.handleKeyDown.bind(this);
        
        this.handleKeyUp = (event) => {
            // Prevent default browser behavior for game controls
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 
                 'KeyW', 'KeyS', 'KeyA', 'KeyD', 'Space', 
                 'ShiftLeft', 'ShiftRight', 'MetaLeft', 'MetaRight'].includes(event.code)) {
                event.preventDefault();
            }
            
            // Update debug display even if car is frozen
            this.pressedKeys.delete(event.code);
            if (this.debug) this.updateDebugDisplay();
            
            console.log('Key up event detected:', event.code);
            
            if (this.playerCar) {
                switch (event.code) {
                    case 'KeyW':
                    case 'ArrowUp':
                        this.inputState.forward = 0;
                        console.log('Forward key released, inputState updated:', this.inputState);
                        break;
                    case 'KeyS':
                    case 'ArrowDown':
                        this.inputState.backward = 0;
                        console.log('Backward key released, inputState updated:', this.inputState);
                        break;
                    case 'KeyA':
                    case 'ArrowLeft':
                        this.inputState.left = 0;
                        console.log('Left key released, inputState updated:', this.inputState);
                        break;
                    case 'KeyD':
                    case 'ArrowRight':
                        this.inputState.right = 0;
                        console.log('Right key released, inputState updated:', this.inputState);
                        break;
                    case 'Space':
                        this.inputState.jump = false;
                        console.log('Jump key released, inputState updated:', this.inputState);
                        break;
                    case 'ShiftLeft':
                    case 'ShiftRight':
                    case 'MetaLeft':
                    case 'MetaRight':
                        this.inputState.boost = false;
                        console.log('Boost key released, inputState updated:', this.inputState);
                        break;
                }
                
                // Update controls immediately for responsiveness
                this.updatePlayerControls();
            } else {
                console.warn('Player car reference is missing, cannot update controls');
            }
        };
        
        // Ensure handleKeyUp is correctly bound to this instance
        this.handleKeyUp = this.handleKeyUp.bind(this);
        
        // Add event listeners
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
        
        console.log('Keyboard event listeners have been set up');
        
        // Add a faster update loop specifically for input handling
        // This makes the controls feel more responsive by updating more frequently
        this.inputLoopInterval = setInterval(() => {
            if (this.playerCar) {
                // Ensure controls are applied consistently
                this.updatePlayerControls();
            }
        }, 8); // Update at ~120Hz for responsive input
    }
    
    handleJoystickMove(event, data) {
        const angle = data.angle.degree;
        const force = Math.min(data.force, 1);
        
        // Reset all directions
        this.inputState.forward = 0;
        this.inputState.backward = 0;
        this.inputState.left = 0;
        this.inputState.right = 0;
        
        // Diagonal inputs - allow partial input in multiple directions
        if (angle >= 0 && angle < 90) {
            // Right-down quadrant
            this.inputState.right = Math.cos(Utils.degToRad(angle)) * force;
            this.inputState.backward = Math.sin(Utils.degToRad(angle)) * force;
        } else if (angle >= 90 && angle < 180) {
            // Left-down quadrant
            this.inputState.left = Math.cos(Utils.degToRad(angle - 90)) * force;
            this.inputState.backward = Math.sin(Utils.degToRad(angle - 90)) * force;
        } else if (angle >= 180 && angle < 270) {
            // Left-up quadrant
            this.inputState.left = Math.cos(Utils.degToRad(angle - 180)) * force;
            this.inputState.forward = Math.sin(Utils.degToRad(angle - 180)) * force;
        } else {
            // Right-up quadrant
            this.inputState.right = Math.cos(Utils.degToRad(angle - 270)) * force;
            this.inputState.forward = Math.sin(Utils.degToRad(angle - 270)) * force;
        }
        
        this.updatePlayerControls();
        if (this.debug) this.updateDebugDisplay();
    }
    
    handleJoystickEnd() {
        // Reset directions
        this.inputState.forward = 0;
        this.inputState.backward = 0;
        this.inputState.left = 0;
        this.inputState.right = 0;
        
        this.updatePlayerControls();
        if (this.debug) this.updateDebugDisplay();
    }
    
    handleTouchStart(event) {
        // Only process touches if not coming from the joystick zone
        if (event.target.closest('#joystick-zone')) return;
        
        // Get the touch point
        const touch = event.touches[0];
        
        // If touch is on the right side of the screen, trigger jump
        if (touch.clientX > window.innerWidth / 2) {
            this.inputState.jump = true;
            
            // If touch is in the upper right, also trigger boost
            if (touch.clientY < window.innerHeight / 2) {
                this.inputState.boost = true;
            }
            
            this.updatePlayerControls();
            if (this.debug) this.updateDebugDisplay();
        }
    }
    
    handleTouchEnd(event) {
        // If no more touches, reset jump and boost
        if (event.touches.length === 0) {
            this.inputState.jump = false;
            this.inputState.boost = false;
            
            this.updatePlayerControls();
            if (this.debug) this.updateDebugDisplay();
        }
    }
    
    updatePlayerControls() {
        // CRITICAL: Always immediately apply controls, the car code now handles movement directly
        if (!this.playerCar) {
            console.warn('Player car reference is missing in InputManager');
            return;
        }
        
        // Force car to be in a state that can receive input
        if (this.playerCar.body) {
            this.playerCar.body.type = CANNON.Body.DYNAMIC;
            this.playerCar.isFrozen = false;
            this.playerCar.body.wakeUp();
        }
        
        // DIRECT CONTROL: Create a fresh controls object
        const controlState = {
            forward: this.inputState.forward,
            backward: this.inputState.backward,
            left: this.inputState.left,
            right: this.inputState.right,
            jump: this.inputState.jump,
            boost: this.inputState.boost
        };
        
        // Apply controls to player car - immediately affect physics!
        this.playerCar.setControls(controlState);
        
        // Force velocity if inputs are active but car isn't moving
        const hasActiveControls = 
            controlState.forward > 0 || 
            controlState.backward > 0 || 
            controlState.left > 0 || 
            controlState.right > 0;
        
        const isMoving = 
            this.playerCar.body && 
            (Math.abs(this.playerCar.body.velocity.x) > 0.5 || 
             Math.abs(this.playerCar.body.velocity.z) > 0.5);
        
        // If controls are active but car isn't moving, log warning and force movement
        if (hasActiveControls && !isMoving && this.playerCar.body) {
            console.warn('Control inputs active but car not moving - forcing direct velocity');
            
            // Get direction vectors
            const carQuat = this.playerCar.mesh.quaternion;
            const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(carQuat);
            
            // Apply direct velocity as a last resort to ensure movement
            if (controlState.forward > 0) {
                this.playerCar.body.velocity.x = forward.x * 20;
                this.playerCar.body.velocity.z = forward.z * 20;
            } else if (controlState.backward > 0) {
                this.playerCar.body.velocity.x = -forward.x * 15;
                this.playerCar.body.velocity.z = -forward.z * 15;
            }
        }
        
        // Debug info
        if (this.debug && Math.random() < 0.01) {
            console.log('Applied controls to player car:', controlState);
            if (this.playerCar && this.playerCar.body) {
                console.log('Player car velocity:', 
                    this.playerCar.body.velocity.x.toFixed(2),
                    this.playerCar.body.velocity.y.toFixed(2),
                    this.playerCar.body.velocity.z.toFixed(2)
                );
            }
        }
    }
    
    cleanup() {
        // Remove event listeners
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
        
        if (this.joystickManager) {
            this.joystickManager.destroy();
        }
        
        document.removeEventListener('touchstart', this.handleTouchStart);
        document.removeEventListener('touchend', this.handleTouchEnd);
        
        // Clear all update intervals
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
        
        if (this.inputLoopInterval) {
            clearInterval(this.inputLoopInterval);
        }
        
        // Remove debug display
        if (this.debugDisplay) {
            document.body.removeChild(this.debugDisplay);
        }
    }
} 